import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import {
  alpha,
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { ApprovalActionType } from '@/types/approvals'
import UserAvatar from '@/components/avatars/UserAvatar'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'

const REASON_MAX_LENGTH = 500

type FormValues = {
  reason: string
}

type ProxyActionDialogProps = {
  overlay: OverlayState
  approverName: string
  approverEmail?: string
  /** The approver's current decision. The dialog starts on the other choice, but either can be picked. */
  currentActionType?: ApprovalActionType | null
  onConfirm: (actionType: ApprovalActionType, reason: string) => void
}

const CHOICES = [
  { value: 'approval', label: 'Approve', Icon: Check, color: 'success' },
  { value: 'rejection', label: 'Reject', Icon: Close, color: 'error' },
] as const

export default function ProxyActionDialog({
  overlay,
  approverName,
  approverEmail,
  currentActionType,
  onConfirm,
}: ProxyActionDialogProps) {
  const defaultActionType: ApprovalActionType =
    currentActionType === 'approval' ? 'rejection' : 'approval'
  const [actionType, setActionType] =
    useState<ApprovalActionType>(defaultActionType)
  const methods = useForm<FormValues>({
    defaultValues: { reason: '' },
    mode: 'onChange',
  })
  const { formState, reset } = methods
  const reasonError = formState.errors.reason
  const isApprove = actionType === 'approval'

  function onSubmit(values: FormValues) {
    onConfirm(actionType, values.reason.trim())
    overlay.close()
  }

  useEffect(() => {
    if (overlay.isOpen) return
    reset({ reason: '' })
    setActionType(defaultActionType)
  }, [overlay.isOpen, reset, defaultActionType])

  return (
    <Dialog
      title="Override approval"
      subtitle="Record a decision for an approver who can't make it themselves."
      {...overlay}
      maxWidth="xs"
      fullWidth
    >
      <PixyDocsForm methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2.5}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
              }}
            >
              <UserAvatar name={approverName} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Acting on behalf of
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {approverName}
                </Typography>
                {approverEmail && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    noWrap
                  >
                    {approverEmail}
                  </Typography>
                )}
              </Box>
            </Stack>

            <ToggleButtonGroup
              exclusive
              fullWidth
              value={actionType}
              onChange={(_event, value: ApprovalActionType | null) => {
                if (value) setActionType(value)
              }}
              aria-label="Decision"
              sx={{ gap: 1.5 }}
            >
              {CHOICES.map(({ value, label, Icon, color }) => (
                <ToggleButton
                  key={value}
                  value={value}
                  sx={(theme) => ({
                    gap: 1,
                    py: 1.25,
                    textTransform: 'none',
                    fontWeight: 600,
                    // Keeps the two choices as separate rounded cards. MUI
                    // otherwise joins grouped buttons into one bar.
                    '&&': {
                      borderRadius: 2,
                      border: `1.5px solid ${theme.palette.divider}`,
                    },
                    '&&.Mui-selected, &&.Mui-selected:hover': {
                      color: theme.palette[color].main,
                      borderColor: theme.palette[color].main,
                      bgcolor: alpha(theme.palette[color].main, 0.1),
                    },
                  })}
                >
                  <Icon fontSize="small" />
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Typography variant="body2" color="text.secondary">
              You&apos;re about to {isApprove ? 'approve' : 'reject'} on behalf
              of {approverName}. Please enter a reason why.
            </Typography>

            <FormTextField
              name="reason"
              autoFocus
              multiline
              minRows={3}
              maxRows={6}
              label="Reason"
              placeholder="e.g. Out of office until Monday"
              fullWidth
              required
              requiredErrorText="A reason is required."
              helperText={reasonError ? reasonError.message : ''}
              error={!!reasonError}
              slotProps={{ htmlInput: { maxLength: REASON_MAX_LENGTH } }}
              useControllerProps={{
                rules: {
                  validate: (value: string) =>
                    value.trim().length ? true : 'A reason is required.',
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color={isApprove ? 'success' : 'error'}
            disabled={!formState.isDirty || !formState.isValid}
          >
            Confirm
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
